// Domain
import useForm from '../../../domain/form/useForm';
import { getFieldsetScrollName } from '../../../domain/form/formUseCase';

// Hooks
import useScrollSpy from '../../../hooks/useScrollSpy';

// Components
import FormLiveSearchPanel from '../../molecules/FormLiveSearchPanel/FormLiveSearchPanel';
import FormTabContent from '../../molecules/FormTabContent/FormTabContent';
import FormTab from '../../atoms/FormTab/FormTab';
import Badge from '../../atoms/Badge/Badge';
import MarkdownContainer from '../../atoms/MarkdownContainer/MarkdownContainer';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';

// Styles
import styles from './FormTabs.module.css';

/**
 * Form Tabs component with react-scroll integration
 * @param {object} props - Component props
 * @param {ApiFormResponse} props.formConfig - Form configuration object
 * @param {Function} [props.onSubmit] - Form submission handler (sanitizedData, submitUrl, submissionParams)
 * @param {(data: ApiFormPostResponse) => void} [props.onLiveSearchAutoSubmitSuccess]
 * @param {(error: Error) => void} [props.onLiveSearchAutoSubmitError]
 * @param {(error: unknown) => void} [props.onLiveSearchFillError]
 * @param {object} [props.options] - Additional form options
 * @param {(isSubmitting: boolean) => React.ReactNode} [props.children] - Form actions
 * @param {string} props.containerId - Container id
 * @param {boolean} props.isLoading - Form loading state
 * @returns {JSX.Element} Form Tabs component
 */
function FormTabs({
  formConfig,
  onSubmit,
  onLiveSearchAutoSubmitSuccess,
  onLiveSearchAutoSubmitError,
  onLiveSearchFillError,
  options = {},
  children,
  containerId,
  isLoading,
}) {
  const {
    register,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
    layout,
    activeTab,
    setActiveTab,
    errorsByTabFieldset,
    fieldsetIds,
    hasMultipleTabs,
    isFieldVisible,
    readOnlyFieldIds,
    disabledFieldIds,
    computed,
    liveSearch: {
      results: liveSearchResults,
      isPending: isLiveSearchPending,
      hasLiveSearch,
      isActionPending,
      queryTerms: liveSearchQueryTerms,
      handleActionClick: handleLiveSearchActionClick,
    },
  } = useForm(
    formConfig,
    onSubmit,
    options,
    0,
    isLoading,
    onLiveSearchAutoSubmitSuccess,
    onLiveSearchAutoSubmitError,
    onLiveSearchFillError,
    containerId,
  );

  const { activeSection, setActiveSection } = useScrollSpy(fieldsetIds);

  /**
   * Handle tab click with ScrollSpy integration
   * @param {number} tabId - ID of the tab to scroll to
   */
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);

    const container = document.getElementById(containerId);
    if (container) {
      container.scrollTop = 0;
    }

    const firstFieldset = layout.tabs[tabId].fieldsets[0];
    if (firstFieldset?.legend) {
      const targetElement = getFieldsetScrollName(containerId, firstFieldset.id);
      setActiveSection(targetElement);
    }
  };

  return (
    isLoading ? (
      <div className={styles['form-loading-container']}>
        <div className={styles['form-loading-fieldsets']}>
          <Skeleton shape="rectangle" width="100%" height="32px" />
          <Skeleton shape="rectangle" width="100%" height="32px" />
        </div>
        <div className={styles['form-loading-content']}>
          <Skeleton shape="rectangle" width="50%" height="32px" />
          <Skeleton shape="rectangle" width="50%" height="32px" />
          <Skeleton shape="rectangle" width="50%" height="32px" />
          <Skeleton shape="rectangle" width="50%" height="32px" />
          <Skeleton shape="rectangle" width="50%" height="32px" />
        </div>
      </div>
    ) : (
      <div
        className={hasLiveSearch ? styles['form-tabs-layout'] : styles['form-tabs-single']}
        data-form-live-search-layout={hasLiveSearch ? '' : undefined}
      >
        <form
          id={hasLiveSearch ? containerId : undefined}
          onSubmit={handleSubmit}
          className={styles['form-tabs']}
        >
          {hasMultipleTabs && (
            <nav className={styles['form-nav-tabs']} id="form-nav-tabs">
              {layout.tabs.map((tab, index) => (
                <FormTab
                  key={tab.id}
                  role="tab"
                  isActive={activeTab === index}
                  ariaSelected={activeTab === index}
                  ariaControls={`tab-panel-${tab.id}`}
                  onClick={() => handleTabClick(tab.id)}
                  disabled={false}
                >
                  <MarkdownContainer content={tab.legend} as="span" />
                  {Object.keys(errorsByTabFieldset[tab.id]).length > 0 ? (
                    <Badge value={Object.keys(errorsByTabFieldset[tab.id]).length} />
                  ) : null}
                </FormTab>
              ))}
            </nav>
          )}

          <div className={styles['form-content']}>
            {hasMultipleTabs ? (
              <div
                id={`tab-panel-${layout.tabs[activeTab].id}`}
                role="tabpanel"
                aria-labelledby={`tab-${layout.tabs[activeTab].id}`}
                className={styles['form-tab-panel']}
              >
                <FormTabContent
                  tab={layout.tabs[activeTab]}
                  register={register}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  layout={layout}
                  errors={errors}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  containerId={containerId}
                  isFieldVisible={isFieldVisible}
                  readOnlyFieldIds={readOnlyFieldIds}
                  disabledFieldIds={disabledFieldIds}
                  computed={computed}
                />
              </div>
            ) : (
              <FormTabContent
                tab={layout.tabs[0]}
                register={register}
                control={control}
                setValue={setValue}
                getValues={getValues}
                layout={layout}
                errors={errors}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                containerId={containerId}
                isFieldVisible={isFieldVisible}
                readOnlyFieldIds={readOnlyFieldIds}
                disabledFieldIds={disabledFieldIds}
                computed={computed}
              />
            )}
          </div>

          <div className={styles['form-actions']}>
            {children ? children(isSubmitting) : null}
          </div>
        </form>

        {hasLiveSearch ? (
          <FormLiveSearchPanel
            results={liveSearchResults}
            isPending={isLiveSearchPending}
            isActionPending={isActionPending}
            highlightTerms={liveSearchQueryTerms}
            onActionClick={handleLiveSearchActionClick}
          />
        ) : null}
      </div>
    )
  );
}

export default FormTabs;
